import { gql, NetworkStatus, useQuery } from "@apollo/client";
import debounce from "lodash/debounce";
import { useCallback, useState } from "react";

import { HISTORY_FILTER_LIMIT } from "@zapier/reporting/src/constants";
import { useSwrQuery } from "@zapier/reporting/src/hooks";
import type {
  ZapsByIdQuery,
  ZapsByIdQueryVariables,
  ZapSearchQuery,
  ZapSearchQueryVariables,
  ZapSearchResultFragment,
} from "./useZapListing.generated";

const zapInfoFragment = gql`
  fragment ZapSearchResult on Zap {
    id
    container @include(if: $includeFolder) {
      __typename
      id
      title
      ... on Folder {
        isShared
      }
    }
    kind
    owner @include(if: $includeOwner) {
      email
      id
      imageUrl
      isCurrentUser
      name
    }
    title
  }
`;

export const getZapListing = gql`
  ${zapInfoFragment}

  query ZapSearch(
    $accountId: ID!
    $includeFolder: Boolean!
    $includeOwner: Boolean!
    $customuserId: ID
    $cursor: String
    $limit: Int
    $search: String
  ) {
    zaps(
      accountId: $accountId
      customuserId: $customuserId
      cursor: $cursor
      limit: $limit
      title: $search
    ) {
      cursor
      edges {
        ...ZapSearchResult
      }
      totalCount
    }
  }
`;

export const getSelectedZaps = gql`
  ${zapInfoFragment}

  query ZapsById(
    $accountId: ID!
    $includeFolder: Boolean!
    $includeOwner: Boolean!
    $ids: [ID!]!
  ) {
    zaps(accountId: $accountId, ids: $ids) {
      edges {
        ...ZapSearchResult
      }
    }
  }
`;

type Input = {
  accountId?: string;
  customuserId?: string;
  /** Request information about the Zap's containing folder */
  includeFolder?: boolean;
  /** Request information about the Zap's owner */
  includeOwner?: boolean;
  zapIds: string[] | undefined;
};

export function useZapListing({
  accountId,
  includeFolder,
  includeOwner,
  customuserId,
  zapIds = [],
}: Input) {
  const [search, setSearch] = useState<string>("");

  const { client, data, error, fetchMore, loading, networkStatus } =
    useSwrQuery<ZapSearchQuery, ZapSearchQueryVariables>(getZapListing, {
      notifyOnNetworkStatusChange: true,
      skip: !accountId,
      variables: {
        accountId: accountId?.toString() ?? "",
        customuserId,
        includeFolder: includeFolder ?? false,
        includeOwner: includeOwner ?? false,
        limit: HISTORY_FILTER_LIMIT,
        search: !search?.includes("Name your zap") ? search : "",
      },
    });

  const selectedZapsResult = useQuery<ZapsByIdQuery, ZapsByIdQueryVariables>(
    getSelectedZaps,
    {
      skip: !accountId || zapIds.length === 0,
      variables: {
        accountId: accountId?.toString() ?? "",
        ids: zapIds,
        includeFolder: includeFolder ?? false,
        includeOwner: includeOwner ?? false,
      },
    }
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSearch = useCallback(
    debounce((inputValue: string) => {
      return setSearch(inputValue || "");
    }, 300),
    []
  );

  // Zaps to be displayed in the filter search results
  const zaps = data?.zaps?.edges || [];

  function onLoadMore() {
    fetchMore({
      updateQuery(prev, { fetchMoreResult }) {
        // We didn't get anything so nothing to update
        if (!fetchMoreResult) return prev;

        // Update the cache
        return {
          ...fetchMoreResult,
          zaps: {
            ...fetchMoreResult?.zaps,
            edges: [
              ...(prev.zaps?.edges || []),
              ...(fetchMoreResult?.zaps?.edges || []),
            ],
          },
        };
      },
      variables: {
        accountId,
        customuserId,
        cursor: data?.zaps?.cursor || undefined,
        limit: HISTORY_FILTER_LIMIT,
        search: !search?.includes("Name your zap") ? search : "",
      },
    });
  }

  function getSelectedZapsCache() {
    const serializedCache: Record<string, any> = client.cache.extract();
    return Object.values(serializedCache).filter((item) => {
      return item.__typename === "Zap" && zapIds.includes(String(item.id));
    }) as ZapSearchResultFragment[];
  }

  // Zaps to be displayed as pills/badges in the UI (selected Zaps)
  const selectedZaps = selectedZapsResult.loading
    ? getSelectedZapsCache()
    : selectedZapsResult.data?.zaps?.edges || [];

  return {
    error,
    isLoading: loading,
    isLoadingMore: networkStatus === NetworkStatus.fetchMore,
    onLoadMore:
      zaps.length < (data?.zaps?.totalCount || 0) ? onLoadMore : undefined,
    onSearch,
    zaps,
    selectedZaps,
  };
}

export default useZapListing;
